/**
 * ProfileWorkItemsPage
 *
 * Two-tab page:
 *  Tab 1 — Work Items  : paginated table, create/edit modal, delete confirm, activate toggle
 *  Tab 2 — Measurements: flat list, create/edit modal, delete confirm (org-owned only), activate toggle
 *
 * Matches the existing emerald/gray Wadakaroo design system exactly.
 */

import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchWorkItems,
    fetchMeasurements,
    createWorkItem,
    updateWorkItem,
    toggleWorkItemActive,
    deleteWorkItem,
    provisionDefaultWorkItems,
    createMeasurement,
    updateMeasurement,
    toggleMeasurementActive,
    deleteMeasurement,
    setActiveTab,
    openCreateWorkItemModal, openEditWorkItemModal, closeWorkItemModal,
    openDeleteWorkItemModal, closeDeleteWorkItemModal,
    openCreateMeasurementModal, openEditMeasurementModal, closeMeasurementModal,
    openDeleteMeasurementModal, closeDeleteMeasurementModal,
    setFilters, setPage,
    setMeasurementsFilter,
    clearOperationSuccess,
    selectWorkItems, selectIsLoading, selectError, selectPagination, selectFilters,
    selectMeasurements, selectIsLoadingMeasurements, selectMeasurementsFilter,
    selectActiveTab,
    selectSelectedWorkItem, selectSelectedMeasurement,
    selectIsWorkItemModalOpen, selectIsDeleteWorkItemModalOpen, selectWorkItemToDelete,
    selectIsMeasurementModalOpen, selectIsDeleteMeasurementModalOpen, selectMeasurementToDelete,
    selectIsCreating, selectIsUpdating, selectIsDeleting, selectIsRefreshing,
    selectIsProvisioning,
    selectOperationError, selectOperationSuccess,
} from '../store/slices/profileWorkItemSlice';
import MainLayout from './Layout.jsx';
import PartLookupPicker from './common/PartLookupPicker';
import Toast from './common/Toast';
import DeleteConfirmModal from './common/DeleteConfirmModal';

// ─── Measurement type options (mirrors Java enum) ──────────────────────────
const MEASUREMENT_TYPES = [
    { value: 'MILLIMETRES',   label: 'mm — Millimetres' },
    { value: 'PSI',           label: 'PSI — Pounds per square inch' },
    { value: 'BAR',           label: 'bar — Bar' },
    { value: 'KILOPASCAL',    label: 'kPa — Kilopascals' },
    { value: 'PERCENTAGE',    label: '% — Percentage' },
    { value: 'VOLTS',         label: 'V — Volts' },
    { value: 'AMPERES',       label: 'A — Amperes' },
    { value: 'NEWTON_METRES', label: 'Nm — Newton metres' },
    { value: 'CELSIUS',       label: '°C — Celsius' },
    { value: 'LITRES',        label: 'L — Litres' },
    { value: 'MILLILITRES',   label: 'mL — Millilitres' },
    { value: 'RPM',           label: 'RPM — Revolutions per minute' },
    { value: 'KILOMETRES',    label: 'km — Kilometres' },
    { value: 'GRAMS',         label: 'g — Grams' },
    { value: 'TEXT',          label: 'Text — Qualitative / descriptive' },
];

// ─── Small shared helpers ──────────────────────────────────────────────────

const Badge = ({ active }) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
    }`}>
        {active ? 'Active' : 'Inactive'}
    </span>
);

const GlobalBadge = () => (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-700">
        Global
    </span>
);

const Spinner = ({ size = 5 }) => (
    <div className={`animate-spin rounded-full w-${size} h-${size} border-2 border-gray-200 border-t-emerald-600`} />
);

const EmptyRow = ({ cols, message }) => (
    <tr>
        <td colSpan={cols} className="py-12 text-center text-gray-400 text-sm">{message}</td>
    </tr>
);

const Pagination = ({ pagination, onPageChange }) => {
    const { page, totalPages, totalElements, hasPrevious, hasNext } = pagination;
    if (totalPages <= 1) return null;
    return (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm text-gray-500">
            <span>{totalElements} total</span>
            <div className="flex items-center gap-2">
                <button
                    onClick={() => onPageChange(page - 1)}
                    disabled={!hasPrevious}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                    Previous
                </button>
                <span className="px-2 font-medium text-gray-700">
                    Page {page + 1} of {totalPages}
                </span>
                <button
                    onClick={() => onPageChange(page + 1)}
                    disabled={!hasNext}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                    Next
                </button>
            </div>
        </div>
    );
};

// ─── Work Item Modal ───────────────────────────────────────────────────────

const WorkItemModal = ({ item, measurements, isCreating, isUpdating, operationError, onClose, onSubmit }) => {
    const isEdit = !!item;
    const [form, setForm] = useState({
        action: item?.action ?? '',
        labourCost: item?.labourCost ?? '',
        estimatedHours: item?.estimatedHours ?? '',
        measurementUuids: item?.measurements?.map(m => m.uuid) ?? [],
        partUuids: item?.parts?.map(p => p.uuid) ?? [],
        selectedParts: item?.parts ?? [],
    });

    const toggleMeasurement = (uuid) => {
        setForm(prev => ({
            ...prev,
            measurementUuids: prev.measurementUuids.includes(uuid)
                ? prev.measurementUuids.filter(u => u !== uuid)
                : [...prev.measurementUuids, uuid],
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit({
            action: form.action.trim(),
            labourCost: form.labourCost !== '' ? parseFloat(form.labourCost) : null,
            estimatedHours: form.estimatedHours !== '' ? parseFloat(form.estimatedHours) : null,
            partUuids: form.partUuids,
            measurementUuids: form.measurementUuids,
        });
    };

    const isBusy = isCreating || isUpdating;
    const activeMeasurements = measurements.filter(m => m.active);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900">
                        {isEdit ? 'Edit Work Item' : 'Create Work Item'}
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
                    <div className="px-6 py-5 space-y-5">
                        {/* Action */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Action <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={form.action}
                                onChange={e => setForm(p => ({ ...p, action: e.target.value }))}
                                placeholder="A task your team performs"
                                required
                                className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                            />
                        </div>

                        {/* Labour cost + Estimated hours */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Labour Cost ($)
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={form.labourCost}
                                    onChange={e => setForm(p => ({ ...p, labourCost: e.target.value }))}
                                    placeholder="0.00"
                                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Est. Hours
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.25"
                                    value={form.estimatedHours}
                                    onChange={e => setForm(p => ({ ...p, estimatedHours: e.target.value }))}
                                    placeholder="0.0"
                                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                                />
                            </div>
                        </div>

                        {/* Parts picker */}
                        <PartLookupPicker
                            selectedUuids={form.partUuids}
                            selectedParts={form.selectedParts}
                            onChange={(uuids, parts) =>
                                setForm(p => ({ ...p, partUuids: uuids, selectedParts: parts }))
                            }
                            maxHeight="max-h-48"
                        />

                        {/* Measurements picker */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Measurements
                                <span className="ml-1.5 text-xs font-normal text-gray-400">
                                    ({form.measurementUuids.length} selected)
                                </span>
                            </label>
                            {activeMeasurements.length === 0 ? (
                                <p className="text-xs text-gray-400 py-3 text-center border border-dashed border-gray-200 rounded-xl">
                                    No measurements available. Create some in the Measurements tab.
                                </p>
                            ) : (
                                <div className="max-h-44 overflow-y-auto border border-gray-200 rounded-xl divide-y divide-gray-100">
                                    {activeMeasurements.map(m => (
                                        <label
                                            key={m.uuid}
                                            className="flex items-center gap-3 px-3.5 py-2.5 hover:bg-gray-50 cursor-pointer"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={form.measurementUuids.includes(m.uuid)}
                                                onChange={() => toggleMeasurement(m.uuid)}
                                                className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                                            />
                                            <span className="flex-1 text-sm text-gray-700">{m.name}</span>
                                            {m.positionLabels?.length > 0 && (
                                                <span className="text-[10px] text-gray-400 font-medium shrink-0">
                                                    {m.positionLabels.join(' · ')}
                                                    {m.subPositionLabels?.length > 0 && (
                                                        <span className="text-emerald-500 ml-1">
                                                            × {m.subPositionLabels.join('/')}
                                                        </span>
                                                    )}
                                                </span>
                                            )}
                                            <span className="text-xs text-gray-400">{m.unitSymbol}</span>
                                            {m.global && <GlobalBadge />}
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Error */}
                        {operationError && (
                            <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                                {operationError}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isBusy}
                            className="inline-flex items-center gap-2 px-5 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-xl hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                        >
                            {isBusy && <Spinner size={4} />}
                            {isEdit ? 'Save Changes' : 'Create Work Item'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// ─── Threshold band config ─────────────────────────────────────────────────

const THRESHOLD_BANDS = [
    {
        key: 'green',
        label: 'Green',
        dotClass: 'bg-emerald-500',
        rowClass: 'bg-emerald-50 border border-emerald-200',
        labelClass: 'text-emerald-700 font-semibold',
        inputClass: 'border-emerald-300 focus:ring-emerald-500 focus:border-emerald-500 text-emerald-900 placeholder-emerald-300',
    },
    {
        key: 'amber',
        label: 'Amber',
        dotClass: 'bg-amber-400',
        rowClass: 'bg-amber-50 border border-amber-200',
        labelClass: 'text-amber-700 font-semibold',
        inputClass: 'border-amber-300 focus:ring-amber-400 focus:border-amber-400 text-amber-900 placeholder-amber-300',
    },
    {
        key: 'red',
        label: 'Red',
        dotClass: 'bg-red-500',
        rowClass: 'bg-red-50 border border-red-200',
        labelClass: 'text-red-700 font-semibold',
        inputClass: 'border-red-300 focus:ring-red-500 focus:border-red-500 text-red-900 placeholder-red-300',
    },
];

/**
 * Parse an existing config value (string or object) into the
 * { green: string, amber: string, red: string } flat shape.
 */
const parseConfigToThresholds = (config) => {
    const defaults = { green: '', amber: '', red: '' };
    if (!config) return defaults;
    try {
        const parsed = typeof config === 'string' ? JSON.parse(config) : config;
        return {
            green: typeof parsed.green === 'string' ? parsed.green : '',
            amber: typeof parsed.amber === 'string' ? parsed.amber : '',
            red:   typeof parsed.red   === 'string' ? parsed.red   : '',
        };
    } catch (_) {
        return defaults;
    }
};

/**
 * Convert thresholds state to the flat config JSON object
 * { green: "...", amber: "...", red: "..." } the backend expects.
 * Only includes bands where a value has been entered.
 */
const buildConfigFromThresholds = (thresholds) => {
    const obj = {};
    for (const band of THRESHOLD_BANDS) {
        const val = thresholds[band.key].trim();
        if (val) obj[band.key] = val;
    }
    return Object.keys(obj).length > 0 ? obj : null;
};

// ─── Measurement Modal ─────────────────────────────────────────────────────

const MeasurementModal = ({ measurement, isCreating, isUpdating, operationError, onClose, onSubmit }) => {
    const isEdit = !!measurement;
    const [form, setForm] = useState({
        name: measurement?.name ?? '',
        measurementType: measurement?.measurementType ?? '',
        thresholds: parseConfigToThresholds(measurement?.config ?? null),
        positionLabels: measurement?.positionLabels ?? [],
        subPositionLabels: measurement?.subPositionLabels ?? [],
    });
    const [positionInput, setPositionInput] = useState('');
    const [subPositionInput, setSubPositionInput] = useState('');

    const setThreshold = (bandKey, val) =>
        setForm(p => ({
            ...p,
            thresholds: { ...p.thresholds, [bandKey]: val },
        }));

    // ── Position label helpers ──
    const addPositionLabel = (label) => {
        const trimmed = label.trim().toUpperCase();
        if (!trimmed || form.positionLabels.includes(trimmed)) return;
        setForm(p => ({ ...p, positionLabels: [...p.positionLabels, trimmed] }));
    };

    const removePositionLabel = (label) => {
        setForm(p => {
            const next = p.positionLabels.filter(l => l !== label);
            // Sub-positions are meaningless without top-level positions — cascade-clear.
            return {
                ...p,
                positionLabels: next,
                subPositionLabels: next.length === 0 ? [] : p.subPositionLabels,
            };
        });
    };

    const handlePositionKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addPositionLabel(positionInput);
            setPositionInput('');
        } else if (e.key === 'Backspace' && !positionInput && form.positionLabels.length > 0) {
            removePositionLabel(form.positionLabels[form.positionLabels.length - 1]);
        }
    };

    const applyPreset = (labels) => {
        setForm(p => ({ ...p, positionLabels: [...labels] }));
    };

    // ── Sub-position label helpers ──
    // Mirror the position-label flow. Sub-positions only become meaningful once
    // positionLabels is populated — UI gates entry, server rejects orphans.
    const addSubPositionLabel = (label) => {
        const trimmed = label.trim().toUpperCase();
        if (!trimmed || form.subPositionLabels.includes(trimmed)) return;
        setForm(p => ({ ...p, subPositionLabels: [...p.subPositionLabels, trimmed] }));
    };

    const removeSubPositionLabel = (label) => {
        setForm(p => ({ ...p, subPositionLabels: p.subPositionLabels.filter(l => l !== label) }));
    };

    const handleSubPositionKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addSubPositionLabel(subPositionInput);
            setSubPositionInput('');
        } else if (e.key === 'Backspace' && !subPositionInput && form.subPositionLabels.length > 0) {
            removeSubPositionLabel(form.subPositionLabels[form.subPositionLabels.length - 1]);
        }
    };

    const applySubPreset = (labels) => {
        setForm(p => ({ ...p, subPositionLabels: [...labels] }));
    };

    const hasPositions = form.positionLabels.length > 0;
    const cellCount = Math.max(1, form.positionLabels.length || 1) *
        Math.max(1, form.subPositionLabels.length || 1);

    // Live preview — derived from current threshold state
    const configPreview = buildConfigFromThresholds(form.thresholds);

    const handleSubmit = (e) => {
        e.preventDefault();
        const config = configPreview; // already the JSON object (or null)
        // Sub-positions only ride along when there are positions to anchor them to.
        const subLabels = hasPositions && form.subPositionLabels.length > 0
            ? form.subPositionLabels
            : null;
        onSubmit({
            name: form.name.trim(),
            measurementType: form.measurementType,
            config: config ? JSON.stringify(config) : null,
            positionLabels: form.positionLabels.length > 0 ? form.positionLabels : null,
            subPositionLabels: subLabels,
        });
    };

    const isBusy = isCreating || isUpdating;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
                <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900">
                        {isEdit ? 'Edit Measurement' : 'Create Measurement'}
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
                    <div className="px-6 py-5 space-y-5">
                        {/* Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={form.name}
                                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                                placeholder="A check you record while performing a work item"
                                required
                                className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                            />
                        </div>

                        {/* Measurement type */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Measurement Type <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={form.measurementType}
                                onChange={e => setForm(p => ({ ...p, measurementType: e.target.value }))}
                                required
                                className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
                            >
                                <option value="">Select type...</option>
                                {MEASUREMENT_TYPES.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* ── Position Labels (multi-slot) ── */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Position Labels
                                <span className="ml-1.5 text-xs font-normal text-gray-400">optional — leave empty for single-slot</span>
                            </label>

                            {/* Presets */}
                            <div className="flex flex-wrap gap-1.5 mb-2">
                                {[
                                    { label: '4 Corners', positions: ['LF', 'RF', 'LR', 'RR'] },
                                    { label: 'Left / Right', positions: ['L', 'R'] },
                                    { label: 'Front / Rear', positions: ['FRONT', 'REAR'] },
                                    { label: '4 Indicators', positions: ['LF', 'RF', 'LR', 'RR'] },
                                ].map(preset => (
                                    <button
                                        key={preset.label}
                                        type="button"
                                        onClick={() => applyPreset(preset.positions)}
                                        className="px-2 py-1 text-[11px] font-medium rounded-lg border border-gray-200 text-gray-500 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700 transition-colors"
                                    >
                                        {preset.label} <span className="text-gray-400 ml-0.5">({preset.positions.join(', ')})</span>
                                    </button>
                                ))}
                                {form.positionLabels.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => setForm(p => ({ ...p, positionLabels: [], subPositionLabels: [] }))}
                                        className="px-2 py-1 text-[11px] font-medium rounded-lg border border-red-200 text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>

                            {/* Tag input */}
                            <div className="flex flex-wrap items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-xl focus-within:ring-2 focus-within:ring-emerald-500 focus-within:border-emerald-500 min-h-[42px] bg-white">
                                {form.positionLabels.map(label => (
                                    <span
                                        key={label}
                                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 rounded-md text-xs font-semibold"
                                    >
                                        {label}
                                        <button
                                            type="button"
                                            onClick={() => removePositionLabel(label)}
                                            className="text-gray-400 hover:text-red-500 transition-colors"
                                        >
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </span>
                                ))}
                                <input
                                    type="text"
                                    value={positionInput}
                                    onChange={e => setPositionInput(e.target.value)}
                                    onKeyDown={handlePositionKeyDown}
                                    onBlur={() => { if (positionInput.trim()) { addPositionLabel(positionInput); setPositionInput(''); } }}
                                    placeholder={form.positionLabels.length === 0 ? 'Type label and press Enter…' : 'Add more…'}
                                    className="flex-1 min-w-[80px] text-sm outline-none bg-transparent placeholder-gray-300"
                                />
                            </div>
                            <p className="mt-1 text-xs text-gray-400">
                                {form.positionLabels.length === 0
                                    ? 'Single-slot — one observation per work item.'
                                    : `${form.positionLabels.length} position${form.positionLabels.length > 1 ? 's' : ''} — one observation per position, all sharing the same thresholds.`
                                }
                            </p>
                        </div>

                        {/* ── Sub-Position Labels (2D grid) ── */}
                        {/*
                            Sub-positions add a second dimension under each top-level position.
                            e.g. positions = [LF,RF,LR,RR] + subs = [OUTER,MIDDLE,INNER]
                                 → 4 x 3 = 12 observation cells per job.
                            Only meaningful when positionLabels is populated — the section is
                            disabled otherwise to match the backend invariant.
                        */}
                        <div className={hasPositions ? '' : 'opacity-50 pointer-events-none select-none'}>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Sub-Position Labels
                                <span className="ml-1.5 text-xs font-normal text-gray-400">
                                    {hasPositions
                                        ? 'optional — adds a second dimension under each position'
                                        : 'add position labels above to enable'}
                                </span>
                            </label>

                            {/* Presets */}
                            <div className="flex flex-wrap gap-1.5 mb-2">
                                {[
                                    { label: 'Tyre Tread', positions: ['OUTER', 'MIDDLE', 'INNER'] },
                                    { label: 'Pad Sides', positions: ['INNER', 'OUTER'] },
                                    { label: 'Front / Rear', positions: ['FRONT', 'REAR'] },
                                ].map(preset => (
                                    <button
                                        key={preset.label}
                                        type="button"
                                        disabled={!hasPositions}
                                        onClick={() => applySubPreset(preset.positions)}
                                        className="px-2 py-1 text-[11px] font-medium rounded-lg border border-gray-200 text-gray-500 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700 transition-colors disabled:cursor-not-allowed"
                                    >
                                        {preset.label} <span className="text-gray-400 ml-0.5">({preset.positions.join(', ')})</span>
                                    </button>
                                ))}
                                {form.subPositionLabels.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => setForm(p => ({ ...p, subPositionLabels: [] }))}
                                        className="px-2 py-1 text-[11px] font-medium rounded-lg border border-red-200 text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>

                            {/* Tag input */}
                            <div className="flex flex-wrap items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-xl focus-within:ring-2 focus-within:ring-emerald-500 focus-within:border-emerald-500 min-h-[42px] bg-white">
                                {form.subPositionLabels.map(label => (
                                    <span
                                        key={label}
                                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-md text-xs font-semibold border border-emerald-200"
                                    >
                                        {label}
                                        <button
                                            type="button"
                                            onClick={() => removeSubPositionLabel(label)}
                                            className="text-emerald-400 hover:text-red-500 transition-colors"
                                        >
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </span>
                                ))}
                                <input
                                    type="text"
                                    value={subPositionInput}
                                    onChange={e => setSubPositionInput(e.target.value)}
                                    onKeyDown={handleSubPositionKeyDown}
                                    onBlur={() => { if (subPositionInput.trim()) { addSubPositionLabel(subPositionInput); setSubPositionInput(''); } }}
                                    disabled={!hasPositions}
                                    placeholder={
                                        !hasPositions
                                            ? 'Disabled — no positions set'
                                            : form.subPositionLabels.length === 0
                                                ? 'Type sub-label and press Enter…'
                                                : 'Add more…'
                                    }
                                    className="flex-1 min-w-[80px] text-sm outline-none bg-transparent placeholder-gray-300 disabled:bg-transparent"
                                />
                            </div>
                            <p className="mt-1 text-xs text-gray-400">
                                {!hasPositions
                                    ? 'Sub-positions require at least one position label above.'
                                    : form.subPositionLabels.length === 0
                                        ? `${form.positionLabels.length} cell${form.positionLabels.length > 1 ? 's' : ''} per job — one row of observations.`
                                        : `${form.positionLabels.length} × ${form.subPositionLabels.length} = ${cellCount} cells per job — every position gets every sub-position.`
                                }
                            </p>
                        </div>

                        {/* ── Threshold Config ── */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Threshold Config
                                <span className="ml-1.5 text-xs font-normal text-gray-400">optional — all bands are independent</span>
                            </label>

                            <div className="space-y-2.5">
                                {THRESHOLD_BANDS.map(band => (
                                    <div
                                        key={band.key}
                                        className={`flex items-center gap-3 px-3.5 py-3 rounded-xl ${band.rowClass}`}
                                    >
                                        {/* Colour indicator + label */}
                                        <div className="flex items-center gap-1.5 w-16 shrink-0">
                                            <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${band.dotClass}`} />
                                            <span className={`text-xs ${band.labelClass}`}>{band.label}</span>
                                        </div>

                                        {/* Value / range */}
                                        <input
                                            type="text"
                                            value={form.thresholds[band.key]}
                                            onChange={e => setThreshold(band.key, e.target.value)}
                                            placeholder="Value / range"
                                            className={`flex-1 min-w-0 px-3 py-2 border rounded-lg text-xs focus:ring-2 outline-none bg-white ${band.inputClass}`}
                                        />
                                    </div>
                                ))}
                            </div>

                            {/* Live preview chips */}
                            {configPreview && (
                                <div className="mt-3 space-y-1">
                                    <p className="text-xs text-gray-400 mb-1.5">Preview — config sent to backend:</p>
                                    <pre className="text-xs font-mono bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-3 text-gray-600 overflow-x-auto whitespace-pre-wrap break-all">
                                        {JSON.stringify(configPreview, null, 2)}
                                    </pre>
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {configPreview.green && (
                                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium border border-emerald-200">
                                                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                                                {configPreview.green}
                                            </span>
                                        )}
                                        {configPreview.amber && (
                                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-50 text-amber-700 rounded-lg text-xs font-medium border border-amber-200">
                                                <span className="w-2 h-2 rounded-full bg-amber-400" />
                                                {configPreview.amber}
                                            </span>
                                        )}
                                        {configPreview.red && (
                                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-50 text-red-700 rounded-lg text-xs font-medium border border-red-200">
                                                <span className="w-2 h-2 rounded-full bg-red-500" />
                                                {configPreview.red}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {operationError && (
                            <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                                {operationError}
                            </div>
                        )}
                    </div>

                    <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50">
                        <button type="button" onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                            Cancel
                        </button>
                        <button type="submit" disabled={isBusy}
                                className="inline-flex items-center gap-2 px-5 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-xl hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors">
                            {isBusy && <Spinner size={4} />}
                            {isEdit ? 'Save Changes' : 'Create Measurement'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// ─── Work Items Table ──────────────────────────────────────────────────────

const WorkItemsTable = ({ items, isLoading, isRefreshing, pagination, onEdit, onDelete, onToggleActive, onPageChange }) => (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="relative overflow-x-auto">
            {isRefreshing && (
                <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
                    <Spinner size={6} />
                </div>
            )}
            <table className="w-full text-sm">
                <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Action</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Labour Cost</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Est. Hours</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Parts</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Measurements</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3" />
                </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                {isLoading ? (
                    <tr><td colSpan={7} className="py-12 text-center"><Spinner size={6} /></td></tr>
                ) : items.length === 0 ? (
                    <EmptyRow cols={7} message="No work items found. Create your first one." />
                ) : (
                    items.map(item => (
                        <tr key={item.uuid}
                            onDoubleClick={() => onEdit(item)}
                            className="hover:bg-gray-50 transition-colors cursor-pointer">
                            <td className="px-4 py-3.5 font-medium text-gray-900">{item.action}</td>
                            <td className="px-4 py-3.5 text-gray-600">
                                {item.labourCost != null ? `$${Number(item.labourCost).toFixed(2)}` : <span className="text-gray-300">—</span>}
                            </td>
                            <td className="px-4 py-3.5 text-gray-600">
                                {item.estimatedHours != null ? `${item.estimatedHours}h` : <span className="text-gray-300">—</span>}
                            </td>
                            <td className="px-4 py-3.5 text-gray-500">
                                {item.parts?.length > 0
                                    ? <span className="font-medium text-gray-700">{item.parts.length}</span>
                                    : <span className="text-gray-300">—</span>}
                            </td>
                            <td className="px-4 py-3.5">
                                {item.measurements?.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                        {item.measurements.slice(0, 2).map(m => (
                                            <span key={m.uuid} className="px-2 py-0.5 bg-sky-50 text-sky-700 rounded text-xs border border-sky-200">
                                                    {m.name}
                                                </span>
                                        ))}
                                        {item.measurements.length > 2 && (
                                            <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs">
                                                    +{item.measurements.length - 2}
                                                </span>
                                        )}
                                    </div>
                                ) : <span className="text-gray-300">—</span>}
                            </td>
                            <td className="px-4 py-3.5"><Badge active={item.active} /></td>
                            <td className="px-4 py-3.5">
                                <div className="flex items-center gap-1 justify-end">
                                    <button onClick={e => { e.stopPropagation(); onToggleActive(item); }}
                                            title={item.active ? 'Deactivate' : 'Activate'}
                                            className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            {item.active
                                                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                                : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />}
                                        </svg>
                                    </button>
                                    <button onClick={e => { e.stopPropagation(); onEdit(item); }}
                                            title="Edit"
                                            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 012.828 2.828L11.828 15H9v-2z" />
                                        </svg>
                                    </button>
                                    <button onClick={e => { e.stopPropagation(); onDelete(item); }}
                                            title="Delete"
                                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))
                )}
                </tbody>
            </table>
        </div>
        <Pagination pagination={pagination} onPageChange={onPageChange} />
    </div>
);

// ─── Measurements Table ────────────────────────────────────────────────────

const MeasurementsTable = ({ measurements, isLoading, pagination, onEdit, onDelete, onToggleActive, onPageChange }) => (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Name</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Type</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Positions</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Thresholds</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Scope</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3" />
                </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                {isLoading ? (
                    <tr><td colSpan={7} className="py-12 text-center"><Spinner size={6} /></td></tr>
                ) : measurements.length === 0 ? (
                    <EmptyRow cols={7} message="No measurements found." />
                ) : (
                    measurements.map(m => {
                        let configPreview = null;
                        if (m.config) { try { configPreview = JSON.parse(m.config); } catch (_) {} }
                        return (
                            <tr key={m.uuid}
                                onDoubleClick={() => !m.global && onEdit(m)}
                                className={`transition-colors ${!m.global ? 'hover:bg-gray-50 cursor-pointer' : ''}`}>
                                <td className="px-4 py-3.5 font-medium text-gray-900">{m.name}</td>
                                <td className="px-4 py-3.5">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                                            {m.unitSymbol || m.measurementType}
                                        </span>
                                </td>
                                <td className="px-4 py-3.5">
                                    {m.positionLabels?.length > 0 ? (
                                        <div className="space-y-1">
                                            <div className="flex flex-wrap gap-1">
                                                {m.positionLabels.map(label => (
                                                    <span key={label} className="inline-flex items-center px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[11px] font-semibold">
                                                        {label}
                                                    </span>
                                                ))}
                                            </div>
                                            {m.subPositionLabels?.length > 0 && (
                                                <div className="flex flex-wrap gap-1">
                                                    {m.subPositionLabels.map(label => (
                                                        <span key={label} className="inline-flex items-center px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded text-[11px] font-semibold">
                                                            {label}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ) : <span className="text-gray-300 text-xs">Single</span>}
                                </td>
                                <td className="px-4 py-3.5">
                                    {configPreview ? (
                                        <div className="flex flex-wrap gap-1">
                                            {configPreview.green && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded text-xs border border-emerald-200"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />{configPreview.green}</span>}
                                            {configPreview.amber && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs border border-amber-200"><span className="w-1.5 h-1.5 rounded-full bg-amber-400" />{configPreview.amber}</span>}
                                            {configPreview.red && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-700 rounded text-xs border border-red-200"><span className="w-1.5 h-1.5 rounded-full bg-red-500" />{configPreview.red}</span>}
                                        </div>
                                    ) : <span className="text-gray-300 text-xs">—</span>}
                                </td>
                                <td className="px-4 py-3.5">
                                    {m.global ? <GlobalBadge /> : <span className="text-xs text-gray-500">Private</span>}
                                </td>
                                <td className="px-4 py-3.5"><Badge active={m.active} /></td>
                                <td className="px-4 py-3.5">
                                    {!m.global && (
                                        <div className="flex items-center gap-1 justify-end">
                                            <button onClick={e => { e.stopPropagation(); onToggleActive(m); }}
                                                    title={m.active ? 'Deactivate' : 'Activate'}
                                                    className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    {m.active
                                                        ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                                        : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />}
                                                </svg>
                                            </button>
                                            <button onClick={e => { e.stopPropagation(); onEdit(m); }}
                                                    title="Edit"
                                                    className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 012.828 2.828L11.828 15H9v-2z" />
                                                </svg>
                                            </button>
                                            <button onClick={e => { e.stopPropagation(); onDelete(m); }}
                                                    title="Delete"
                                                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    )}
                                </td>
                            </tr>
                        );
                    })
                )}
                </tbody>
            </table>
        </div>
        <Pagination pagination={pagination} onPageChange={onPageChange} />
    </div>
);

// ─── Page ──────────────────────────────────────────────────────────────────

const ProfileWorkItemsPage = () => {
    const dispatch = useDispatch();

    // Work items
    const workItems     = useSelector(selectWorkItems);
    const isLoading     = useSelector(selectIsLoading);
    const error         = useSelector(selectError);
    const pagination    = useSelector(selectPagination);
    const filters       = useSelector(selectFilters);
    const isRefreshing  = useSelector(selectIsRefreshing);

    // Measurements
    const measurements          = useSelector(selectMeasurements);
    const isLoadingMeasurements = useSelector(selectIsLoadingMeasurements);
    const measurementsFilter    = useSelector(selectMeasurementsFilter);

    // Client-side pagination for the Measurements tab.
    // The full measurements list stays in Redux so the Work Item picker can
    // see every option; this slice is only for the tab's table display.
    const MEASUREMENTS_PAGE_SIZE = 12;
    const [measurementsPage, setMeasurementsPage] = useState(0);

    // Provisioning starter work items (empty-state action)
    const isProvisioning = useSelector(selectIsProvisioning);
    const handleProvisionDefaults = useCallback(
        () => dispatch(provisionDefaultWorkItems()),
        [dispatch]
    );

    // Reset to page 1 when the filter changes — otherwise a filter that
    // shrinks the list could leave us on a now-empty page.
    useEffect(() => {
        setMeasurementsPage(0);
    }, [measurementsFilter.activeOnly]);

    const paginatedMeasurements = useMemo(() => {
        const start = measurementsPage * MEASUREMENTS_PAGE_SIZE;
        return measurements.slice(start, start + MEASUREMENTS_PAGE_SIZE);
    }, [measurements, measurementsPage]);

    const measurementsPagination = useMemo(() => {
        const totalElements = measurements.length;
        const totalPages = Math.max(1, Math.ceil(totalElements / MEASUREMENTS_PAGE_SIZE));
        return {
            page: measurementsPage,
            size: MEASUREMENTS_PAGE_SIZE,
            totalElements,
            totalPages,
            hasPrevious: measurementsPage > 0,
            hasNext: measurementsPage < totalPages - 1,
        };
    }, [measurements.length, measurementsPage]);

    // Tab
    const activeTab = useSelector(selectActiveTab);

    // Modals
    const selectedWorkItem    = useSelector(selectSelectedWorkItem);
    const selectedMeasurement = useSelector(selectSelectedMeasurement);

    const isWorkItemModalOpen        = useSelector(selectIsWorkItemModalOpen);
    const isDeleteWorkItemModalOpen  = useSelector(selectIsDeleteWorkItemModalOpen);
    const workItemToDelete           = useSelector(selectWorkItemToDelete);

    const isMeasurementModalOpen       = useSelector(selectIsMeasurementModalOpen);
    const isDeleteMeasurementModalOpen = useSelector(selectIsDeleteMeasurementModalOpen);
    const measurementToDelete          = useSelector(selectMeasurementToDelete);

    // Ops
    const isCreating       = useSelector(selectIsCreating);
    const isUpdating       = useSelector(selectIsUpdating);
    const isDeleting       = useSelector(selectIsDeleting);
    const operationError   = useSelector(selectOperationError);
    const operationSuccess = useSelector(selectOperationSuccess);

    // ── Data fetching ──

    useEffect(() => {
        dispatch(fetchWorkItems({ page: pagination.page, size: pagination.size, activeOnly: filters.activeOnly }));
    }, [dispatch, pagination.page, pagination.size, filters.activeOnly]);

    // Always keep measurements list fresh (used in picker + Measurements tab).
    // Fetch a large bucket up-front so the Work Item picker has every option;
    // the tab itself paginates this list client-side.
    useEffect(() => {
        dispatch(fetchMeasurements({ page: 0, size: 200, activeOnly: measurementsFilter.activeOnly }));
    }, [dispatch, measurementsFilter.activeOnly]);

    // Refetch work items after success
    useEffect(() => {
        if (operationSuccess) {
            dispatch(fetchWorkItems({ page: pagination.page, size: pagination.size, activeOnly: filters.activeOnly }));
            // Also refresh measurements — a successful create/update/delete on a
            // measurement (or a status toggle) needs to be reflected in both the
            // tab table and the Work Item picker.
            dispatch(fetchMeasurements({ page: 0, size: 200, activeOnly: measurementsFilter.activeOnly }));
            const t = setTimeout(() => dispatch(clearOperationSuccess()), 3000);
            return () => clearTimeout(t);
        }
    }, [operationSuccess, dispatch, pagination.page, pagination.size, filters.activeOnly, measurementsFilter.activeOnly]);

    // ── Work Item handlers ──

    const handleCreateWorkItem = useCallback(() => dispatch(openCreateWorkItemModal()), [dispatch]);
    const handleEditWorkItem   = useCallback((item) => dispatch(openEditWorkItemModal(item)), [dispatch]);
    const handleDeleteWorkItem = useCallback((item) => dispatch(openDeleteWorkItemModal(item)), [dispatch]);

    const handleToggleWorkItemActive = useCallback((item) => {
        dispatch(toggleWorkItemActive({ uuid: item.uuid, active: !item.active }));
    }, [dispatch]);

    const handleSubmitWorkItem = useCallback((data) => {
        if (selectedWorkItem) {
            dispatch(updateWorkItem({ uuid: selectedWorkItem.uuid, data }));
        } else {
            dispatch(createWorkItem(data));
        }
    }, [dispatch, selectedWorkItem]);

    const handleConfirmDeleteWorkItem = useCallback(() => {
        if (workItemToDelete) dispatch(deleteWorkItem(workItemToDelete.uuid));
    }, [dispatch, workItemToDelete]);

    // ── Measurement handlers ──

    const handleCreateMeasurement = useCallback(() => dispatch(openCreateMeasurementModal()), [dispatch]);
    const handleEditMeasurement   = useCallback((m) => dispatch(openEditMeasurementModal(m)), [dispatch]);
    const handleDeleteMeasurement = useCallback((m) => dispatch(openDeleteMeasurementModal(m)), [dispatch]);

    const handleToggleMeasurementActive = useCallback((m) => {
        dispatch(toggleMeasurementActive({ uuid: m.uuid, active: !m.active }));
    }, [dispatch]);

    const handleSubmitMeasurement = useCallback((data) => {
        if (selectedMeasurement) {
            dispatch(updateMeasurement({ uuid: selectedMeasurement.uuid, data }));
        } else {
            dispatch(createMeasurement(data));
        }
    }, [dispatch, selectedMeasurement]);

    const handleConfirmDeleteMeasurement = useCallback(() => {
        if (measurementToDelete) dispatch(deleteMeasurement(measurementToDelete.uuid));
    }, [dispatch, measurementToDelete]);

    // ── Render ──

    return (
        <MainLayout>
            <div className="p-6">

                {/* Page Header */}
                <div className="mb-6">
                    <h1 className="text-2xl font-display font-bold text-gray-900">
                        Work Items
                    </h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Define reusable work items and measurements for service templates.
                    </p>
                </div>

                {/* Tabs */}
                <div className="mb-6 border-b border-gray-200">
                    <nav className="flex gap-1 -mb-px">
                        {[
                            { key: 'workItems',    label: 'Work Items',    count: pagination.totalElements },
                            { key: 'measurements', label: 'Measurements',  count: measurements.length },
                        ].map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => dispatch(setActiveTab(tab.key))}
                                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                                    activeTab === tab.key
                                        ? 'border-emerald-600 text-emerald-700'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                {tab.label}
                                <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-semibold ${
                                    activeTab === tab.key
                                        ? 'bg-emerald-100 text-emerald-700'
                                        : 'bg-gray-100 text-gray-500'
                                }`}>
                                    {tab.count}
                                </span>
                            </button>
                        ))}
                    </nav>
                </div>

                {/* ── Work Items Tab ── */}
                {activeTab === 'workItems' && (
                    <>
                        {/* Toolbar */}
                        <div className="mb-4 flex flex-col sm:flex-row gap-3 justify-between">
                            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={filters.activeOnly}
                                    onChange={e => dispatch(setFilters({ activeOnly: e.target.checked }))}
                                    className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                                />
                                Active only
                            </label>
                            <div className="flex items-center gap-2 self-start sm:self-auto">
                                {!isLoading && workItems.length === 0 && (
                                    <button
                                        onClick={handleProvisionDefaults}
                                        disabled={isProvisioning}
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-60"
                                    >
                                        {isProvisioning ? (
                                            <Spinner size={4} />
                                        ) : (
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                            </svg>
                                        )}
                                        Add starter work items
                                    </button>
                                )}
                                <button
                                    onClick={handleCreateWorkItem}
                                    className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl transition-colors ${
                                        !isLoading && workItems.length === 0
                                            ? 'bg-white text-emerald-700 border border-emerald-300 hover:bg-emerald-50'
                                            : 'bg-emerald-600 text-white hover:bg-emerald-700'
                                    }`}
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                    New Work Item
                                </button>
                            </div>
                        </div>

                        {error && (
                            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>
                        )}

                        <WorkItemsTable
                            items={workItems}
                            isLoading={isLoading}
                            isRefreshing={isRefreshing}
                            pagination={pagination}
                            onEdit={handleEditWorkItem}
                            onDelete={handleDeleteWorkItem}
                            onToggleActive={handleToggleWorkItemActive}
                            onPageChange={page => dispatch(setPage(page))}
                        />
                    </>
                )}

                {/* ── Measurements Tab ── */}
                {activeTab === 'measurements' && (
                    <>
                        <div className="mb-4 flex flex-col sm:flex-row gap-3 justify-between">
                            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={measurementsFilter.activeOnly}
                                    onChange={e => dispatch(setMeasurementsFilter({ activeOnly: e.target.checked }))}
                                    className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                                />
                                Active only
                            </label>
                            <div className="flex items-center gap-2 self-start sm:self-auto">
                                <span className="text-xs text-gray-400 mr-1">
                                    <GlobalBadge /> = shared across all workshops
                                </span>
                                <button
                                    onClick={handleCreateMeasurement}
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-xl hover:bg-emerald-700 transition-colors"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                    New Measurement
                                </button>
                            </div>
                        </div>

                        <MeasurementsTable
                            measurements={paginatedMeasurements}
                            isLoading={isLoadingMeasurements}
                            pagination={measurementsPagination}
                            onEdit={handleEditMeasurement}
                            onDelete={handleDeleteMeasurement}
                            onToggleActive={handleToggleMeasurementActive}
                            onPageChange={setMeasurementsPage}
                        />
                    </>
                )}
            </div>

            {/* ── Modals ── */}

            {isWorkItemModalOpen && (
                <WorkItemModal
                    item={selectedWorkItem}
                    measurements={measurements}
                    isCreating={isCreating}
                    isUpdating={isUpdating}
                    operationError={operationError}
                    onClose={() => dispatch(closeWorkItemModal())}
                    onSubmit={handleSubmitWorkItem}
                />
            )}

            {isDeleteWorkItemModalOpen && workItemToDelete && (
                <DeleteConfirmModal
                    title="Delete Work Item"
                    message={`Are you sure you want to delete "${workItemToDelete.action}"? This action cannot be undone.`}
                    confirmLabel="Delete"
                    isDestructive
                    isLoading={isDeleting}
                    canConfirm
                    error={operationError}
                    onConfirm={handleConfirmDeleteWorkItem}
                    onCancel={() => dispatch(closeDeleteWorkItemModal())}
                />
            )}

            {isMeasurementModalOpen && (
                <MeasurementModal
                    measurement={selectedMeasurement}
                    isCreating={isCreating}
                    isUpdating={isUpdating}
                    operationError={operationError}
                    onClose={() => dispatch(closeMeasurementModal())}
                    onSubmit={handleSubmitMeasurement}
                />
            )}

            {isDeleteMeasurementModalOpen && measurementToDelete && (
                <DeleteConfirmModal
                    title="Delete Measurement"
                    message={`Are you sure you want to delete "${measurementToDelete.name}"? This action cannot be undone.`}
                    confirmLabel="Delete"
                    isDestructive
                    isLoading={isDeleting}
                    canConfirm
                    error={operationError}
                    onConfirm={handleConfirmDeleteMeasurement}
                    onCancel={() => dispatch(closeDeleteMeasurementModal())}
                />
            )}

            {operationSuccess && (
                <Toast
                    message={operationSuccess}
                    type="success"
                    onClose={() => dispatch(clearOperationSuccess())}
                />
            )}
        </MainLayout>
    );
};

export default ProfileWorkItemsPage;